/**
 * Suche über den lokalen Cache – ohne Netzzugriff.
 *
 * Das ist der Grund für den ganzen Cache: ein Suchfeld, das auf eine Antwort
 * aus dem Rechenzentrum wartet, fühlt sich immer träge an, egal wie schnell
 * der Server ist. Zielwert ist unter 150 ms; bei den Beständen eines kleinen
 * Betriebs liegt die Wirklichkeit im einstelligen Millisekundenbereich.
 *
 * Die Rangfolge kommt aus AV Parkett Office und hat sich dort bewährt:
 * exakter Code zuerst, dann Anfang des Codes, dann Anfang des Namens, dann
 * Wortanfang, dann irgendwo enthalten. Ohne Rangfolge findet man "Fischgrat"
 * bei der Eingabe "parkett" zwar, aber auf Platz vierzig.
 */

export interface Searchable {
  id: string
  [field: string]: unknown
}

export interface SearchOptions<T> {
  /** Felder, über die gesucht wird – in absteigender Wichtigkeit. */
  fields: Array<keyof T & string>
  /** Feld, das als "Code" gilt und exakte Treffer nach vorne bringt. */
  codeField?: keyof T & string
  /** Feld, das als Hauptbezeichnung gilt. */
  nameField?: keyof T & string
  limit?: number
}

/** Diakritika und Schreibweisen vereinheitlichen: "Zürich" findet man auch mit "zurich". */
export function normalize(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss')
}

export function tokenize(query: string): string[] {
  return normalize(query).split(/\s+/).filter(Boolean)
}

function rank<T extends Searchable>(
  row: T,
  tokens: string[],
  options: SearchOptions<T>
): number {
  const haystack = options.fields.map((f) => normalize(row[f])).join(' ')
  // Alle Suchbegriffe müssen vorkommen – "fisch parkett" findet nur Positionen,
  // in denen beides steht.
  if (!tokens.every((t) => haystack.includes(t))) return -1

  const first = tokens[0]
  const code = options.codeField ? normalize(row[options.codeField]) : ''
  const name = options.nameField ? normalize(row[options.nameField]) : ''

  if (code && code === first) return 0
  if (code && code.startsWith(first)) return 1
  if (name && name.startsWith(first)) return 2
  if (new RegExp(`\\b${first.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(haystack)) return 3
  return 4
}

/**
 * Reihenfolge ohne Suchbegriff: nach Code, sonst nach Name.
 *
 * Ohne das erscheint die Liste in der Reihenfolge, in der der Cache sie
 * zurückgibt – für die Datenbank sinnvoll, für einen Katalog, den jemand
 * durchblättert, unbrauchbar.
 */
function ordne<T extends Searchable>(rows: readonly T[], options: SearchOptions<T>): T[] {
  const key = (row: T): string =>
    options.codeField && String(row[options.codeField] ?? '').trim()
      ? normalize(row[options.codeField])
      : normalize(options.nameField ? row[options.nameField] : row.id)
  return [...rows].sort((a, b) => key(a).localeCompare(key(b), 'de-CH', { numeric: true }))
}

export function search<T extends Searchable>(
  rows: readonly T[],
  query: string,
  options: SearchOptions<T>
): T[] {
  const tokens = tokenize(query)
  if (tokens.length === 0) return ordne(rows, options).slice(0, options.limit ?? 50)

  const scored: Array<{ row: T; score: number }> = []
  for (const row of rows) {
    const score = rank(row, tokens, options)
    if (score >= 0) scored.push({ row, score })
  }
  scored.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score
    const an = options.nameField ? normalize(a.row[options.nameField]) : ''
    const bn = options.nameField ? normalize(b.row[options.nameField]) : ''
    return an.localeCompare(bn)
  })
  return scored.slice(0, options.limit ?? 50).map((s) => s.row)
}

export const CUSTOMER_SEARCH: SearchOptions<Searchable> = {
  fields: ['name', 'contact', 'street', 'zip', 'city', 'email', 'phone', 'notes'],
  nameField: 'name',
  limit: 50
}

export const PROJECT_SEARCH: SearchOptions<Searchable> = {
  fields: ['name', 'street', 'zip', 'city', 'notes'],
  nameField: 'name',
  limit: 50
}

export const CATALOG_SEARCH: SearchOptions<Searchable> = {
  fields: ['code', 'name', 'unit'],
  codeField: 'code',
  nameField: 'name',
  limit: 50
}
